/**
 * ChangeOrderPostingBridge — Wave 1.
 *
 * Mounts inside <GeneralLedgerProvider> + reads jobs from AppData.
 * Posts an AR / Deferred-Revenue entry the moment a job's change order
 * transitions to "Signed". Idempotent on change-order id, so re-renders,
 * HMR, and seed reruns post exactly once.
 *
 * Separate from AutoPostingBridge to keep concerns scoped: this one's
 * only contract is "job.changeOrders → GL".
 */
import { useEffect, useRef } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useGeneralLedger } from "./GeneralLedgerContext";
import { ruleChangeOrderApproved } from "./postingRules";

const key = (id: string) => `change_order::${id}`;

export function ChangeOrderPostingBridge() {
  const { jobs } = useAppData();
  const { journalEntries, postJournal } = useGeneralLedger();
  const postedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    for (const je of journalEntries) {
      if (je.sourceRef?.kind === "change_order") postedRef.current.add(key(je.sourceRef.id));
    }
  }, [journalEntries]);

  useEffect(() => {
    for (const job of jobs) {
      const cos = job.changeOrders ?? [];
      for (const co of cos) {
        const k = key(co.id);
        if (postedRef.current.has(k)) continue;
        const rule = ruleChangeOrderApproved(job, co);
        if (!rule) continue;
        try {
          postJournal(rule);
          postedRef.current.add(k);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`[ChangeOrderPostingBridge] CO ${co.id}:`, e instanceof Error ? e.message : e);
        }
      }
    }
  }, [jobs, postJournal]);

  return null;
}
